/**
 * Utilitaires pour détecter les capacités de performance de l'appareil
 * et optimiser les animations en conséquence
 */

/**
 * Détecte si l'appareil préfère des animations réduites
 * @returns true si l'utilisateur préfère des animations réduites
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Détecte si l'appareil est considéré comme ayant des performances limitées
 * @returns true si l'appareil est considéré comme moins performant
 */
export function isLowPerformanceDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Vérifie le nombre de cœurs processeur (si disponible)
  const hardwareConcurrency = navigator.hardwareConcurrency;
  if (hardwareConcurrency && hardwareConcurrency < 4) {
    return true;
  }

  // Vérifie la mémoire disponible (si disponible)
  const deviceMemory = navigator.deviceMemory;
  if (typeof deviceMemory === 'number' && deviceMemory < 4) {
    return true;
  }

  // Détecte les anciens appareils mobiles via l'user agent
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

  if (isMobile) {
    // Détecte les anciens iOS
    const iosVersion = userAgent.match(/os (\d+)_/);
    if (iosVersion && parseInt(iosVersion[1]) < 13) {
      return true;
    }

    // Détecte les anciens Android
    const androidVersion = userAgent.match(/android (\d+)/);
    if (androidVersion && parseInt(androidVersion[1]) < 8) {
      return true;
    }
  }

  return false;
}

/**
 * Détermine si les animations complexes doivent être activées
 * @returns true si les animations complexes peuvent être utilisées
 */
export function shouldUseComplexAnimations(): boolean {
  return !prefersReducedMotion() && !isLowPerformanceDevice();
}

/**
 * Retourne des options de transition optimisées pour framer-motion
 */
export type OptimizedTransition = {
  duration: number;
  ease: string | string[];
};

export function getOptimizedTransition(duration: number = 0.6): OptimizedTransition {
  const useComplex = shouldUseComplexAnimations();

  return {
    duration: useComplex ? duration : duration * 0.5,
    ease: useComplex ? 'easeOut' : 'linear',
  };
}
